import * as Tooltip from '@radix-ui/react-tooltip';
import { AdaptivePhoto } from '../photos/AdaptivePhoto';
import { MarkupCanvas } from '../markup/MarkupCanvas';
import type { MarkupShape } from '../../types/markup.types';
import type { Photo } from '../../types/photo';
import type { HeroPhoto } from '../../types/critique-session.types';
import './ShapeReference.css';

interface ShapeReferenceProps {
  shapeNumber: number;
  photo: Photo | HeroPhoto;
  shapes: MarkupShape[];
  onClick: () => void;
}

/**
 * ShapeReference component
 * Feature: 050-critique-markup-integration
 *
 * Interactive @-mention for markup shapes (@1-@9).
 * - Hover: Shows tooltip with 600x600px preview (3x larger for visibility)
 * - Click: Opens full-size MarkupPreviewModal
 */
export function ShapeReference({ shapeNumber, photo, shapes, onClick }: ShapeReferenceProps) {
  // Filter shapes to only show the referenced one
  const referencedShape = shapes.find(s => s.number === shapeNumber);
  const previewShapes = referencedShape ? [referencedShape] : [];

  // Calculate photo aspect ratio and preview dimensions
  const photoAspectRatio = (() => {
    const variant = photo.variants.find((v) => v.width && v.height);
    if (variant && variant.width && variant.height) {
      return variant.width / variant.height;
    }
    return 1;
  })();

  // Calculate preview dimensions (max 600x600, maintaining aspect ratio)
  const maxDimension = 600;
  let previewWidth = maxDimension;
  let previewHeight = maxDimension;

  if (photoAspectRatio > 1) {
    // Landscape - width is limiting factor
    previewHeight = maxDimension / photoAspectRatio;
  } else {
    // Portrait or square - height is limiting factor
    previewWidth = maxDimension * photoAspectRatio;
  }

  return (
    <Tooltip.Provider delayDuration={300}>
      <Tooltip.Root>
        <Tooltip.Trigger asChild>
          <span
            className="shape-reference"
            data-shape={shapeNumber}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClick();
            }}
          >
            @{shapeNumber}
          </span>
        </Tooltip.Trigger>

        {previewShapes.length > 0 && (
          <Tooltip.Portal>
            <Tooltip.Content
              side="top"
              className="shape-reference-preview"
              sideOffset={8}
              onPointerDownOutside={(e) => e.preventDefault()}
            >
              <div
                className="shape-reference-preview-container"
                style={{ width: `${previewWidth}px`, height: `${previewHeight}px` }}
              >
                {/* Photo with markup overlay */}
                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                  <AdaptivePhoto photo={photo} className="w-full h-full object-cover" />

                  {/* Markup canvas overlay */}
                  <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                    <MarkupCanvas
                      shapes={previewShapes}
                      selectedShapeId={null}
                      readOnly={true}
                      photoAspectRatio={photoAspectRatio}
                    />
                  </div>
                </div>

                {/* Shape label */}
                <div className="shape-reference-preview-label">
                  Shape {shapeNumber}
                </div>
              </div>
              <Tooltip.Arrow className="shape-reference-preview-arrow" />
            </Tooltip.Content>
          </Tooltip.Portal>
        )}
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}
